import {
    Button,
    Field,
    Input,
    createTableColumn,
} from "@fluentui/react-components";
import { bindInputValue } from "../../form_utils";
import { FieldItemDropdown } from "../../services/field_item_service";
import { UserDropdown } from "../../services/user_service";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useContext, useEffect, useState } from "react";
import { SimpleGrid } from "../../components/simple_grid";
import { Context } from "../../context";
import { toDateString, toDateTimeString, yyyymmdd } from "../../date_utils";
import { useNavigate } from "react-router-dom";
import { idString } from "../../db_utils";

const listReport = async (apiClient, filters, currentField) => {
    try {
        const params = {
            status: 'report',
            ...filters
        };
        // Get current field from Context parameter
        if (currentField && currentField.id) {
            params.fieldId = currentField.id;
        }
        const reports = await apiClient.getTasks(params);
        return reports || [];
    } catch (error) {
        console.error("Failed to load task reports:", error);
        return [];
    }
};

const listSessions = async (apiClient) => {
    try {
        // Get released sessions
        const sessions = await apiClient.get('/inspectionsession?reviewStatus=released');
        return sessions || [];
    } catch (error) {
        console.error("Failed to load released sessions:", error);
        return [];
    }
};

export const TaskReport = () => {
    const [filters, setFilters] = useState({});
    const { apiClient, currentField } = useContext(Context);
    const [items, setItems] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [viewMode, setViewMode] = useState("tasks"); // "tasks" or "sessions"

    const loadReports = async () => {
        let items = await listReport(apiClient, filters, currentField);
        setItems(items);
    };

    const loadSessions = async () => {
        let sessionData = await listSessions(apiClient);
        setSessions(sessionData);
    };

    const nav = useNavigate();
    useEffect(() => {
        if (!apiClient) {
            return;
        }
        loadReports();
        loadSessions();
    }, [apiClient, filters, currentField]);
    const doExportReport = async (item) => {
        nav(`/task/report/${item.id}`);
    };

    const doViewSessionReport = async (session) => {
        let reportTaskId = session.reportTaskId || session.report_task_id;

        if (!reportTaskId) {
            try {
                const tasks = await apiClient.getInspectionSessionTasks(session.id);
                const nonRootTasks = (tasks || []).filter((task) => {
                    const name = task.name || "";
                    return !name.endsWith("ROOT") && task.status !== "deleted";
                });
                const releasedTask = nonRootTasks.find((task) =>
                    task.status === "released" ||
                    task.status === "published" ||
                    task.reviewStatus === "released" ||
                    task.reviewStatus === "published"
                );
                reportTaskId = releasedTask?.id || nonRootTasks[0]?.id;
            } catch (error) {
                console.error("Failed to load session report task:", error);
            }
        }

        if (!reportTaskId) {
            console.error("No report task found for session:", session);
            return;
        }

        nav(`/task/report/${reportTaskId}`);
    };
    const columns = [
        createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "报告名称";
            },
            renderCell: (item) => {
                return item.name;
            },
        }),
        createTableColumn({
            columnId: "field_item",
            renderHeaderCell: () => {
                return "风场名称";
            },
            renderCell: (item) => {
                return item.turbines?.[0]?.field_item?.name || "";
            },
        }),
        createTableColumn({
            columnId: "turbineCount",
            renderHeaderCell: () => {
                return "风机数";
            },
            renderCell: (item) => {
                return item.turbines?.length || 0;
            },
        }),
        createTableColumn({
            columnId: "",
            renderHeaderCell: () => {
                return "风机名称";
            },
            renderCell: (item) => {
                return (item.turbines || []).map((it) => it?.name).join(", ");
            },
        }),
        createTableColumn({
            columnId: "image_count",
            renderHeaderCell: () => {
                return "照片";
            },
            renderCell: (item) => {
                return item.image_count;
            },
        }),
        createTableColumn({
            columnId: "defection_count",
            renderHeaderCell: () => {
                return "缺陷";
            },
            renderCell: (item) => {
                return item.defection_count;
            },
        }),
        createTableColumn({
            columnId: "checker",
            renderHeaderCell: () => {
                return "巡检负责人";
            },
            renderCell: (item) => {
                return item.owner_checker?.name;
            },
        }),
        createTableColumn({
            columnId: "rechecker",
            renderHeaderCell: () => {
                return "缺陷复核人";
            },
            renderCell: (item) => {
                return item.owner_rechecker?.name;
            },
        }),
        createTableColumn({
            columnId: "updated_at",
            renderHeaderCell: () => {
                return "生成时间";
            },
            renderCell: (item) => {
                return toDateTimeString(item.updated_at);
            },
        }),
        createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => {
                return "操作";
            },
            renderCell: (item) => {
                return (
                    <div>
                        <Button
                            appearance="subtle"
                            onClick={() => doExportReport(item)}
                        >
                            查看报告
                        </Button>
                    </div>
                );
            },
        }),
    ];

    // Define session columns
    const sessionColumns = [
        createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => "会话编号",
            renderCell: (item) => item.seq
        }),
        createTableColumn({
            columnId: "sessionName",
            renderHeaderCell: () => "会话名称",
            renderCell: (item) => item.sessionName
        }),
        createTableColumn({
            columnId: "fieldItemName",
            renderHeaderCell: () => "风场名称",
            renderCell: (item) => item.fieldItemName
        }),
        createTableColumn({
            columnId: "taskCount",
            renderHeaderCell: () => "任务数",
            renderCell: (item) => item.taskCount || 0
        }),
        createTableColumn({
            columnId: "releasedAt",
            renderHeaderCell: () => "发布时间",
            renderCell: (item) => item.releasedAt ? toDateTimeString(item.releasedAt) : "-"
        }),
        createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => "操作",
            renderCell: (item) => (
                <div>
                    <Button
                        appearance="subtle"
                        onClick={() => doViewSessionReport(item)}
                    >
                        查看报告
                    </Button>
                </div>
            )
        })
    ];

    const v = bindInputValue(null, filters, setFilters);
    return (
        <div className="flex flex-col gap-8 p-2">
            <div className="flex gap-4 mb-4">
                <Button
                    appearance={viewMode === "tasks" ? "primary" : "outline"}
                    onClick={() => setViewMode("tasks")}
                >
                    任务报告 ({items.length})
                </Button>
                <Button
                    appearance={viewMode === "sessions" ? "primary" : "outline"}
                    onClick={() => setViewMode("sessions")}
                >
                    已发布会话 ({sessions.length})
                </Button>
            </div>
            <div className="grid grid-cols-4 gap-4">
                <Field label="报告名称">
                    <Input {...v("name")} />
                </Field>
                <Field label="风机名称">
                    <Input {...v("turbineName")} />
                </Field>
                <Field label="风场名称">
                    <FieldItemDropdown
                        onOptionSelect={(_, data) => {
                            setFilters({
                                ...filters,
                                fieldItem: data.optionValue,
                            });
                        }}
                    />
                </Field>
                <Field label="巡检负责人">
                    <UserDropdown
                        onOptionSelect={(_, data) => {
                            setFilters({
                                ...filters,
                                checker: data.optionValue,
                            });
                        }}
                    />
                </Field>
                <Field label="缺陷复核人">
                    <UserDropdown
                        onOptionSelect={(_, data) => {
                            setFilters({
                                ...filters,
                                rechecker: data.optionValue,
                            });
                        }}
                    />
                </Field>
                <Field label="生成日期">
                    <DatePicker
                        formatDate={toDateString}
                        onSelectDate={(date) => {
                            setFilters({
                                ...filters,
                                created_at: yyyymmdd(date),
                            });
                        }}
                    />
                </Field>
                <div className="flex-1"></div>
            </div>
            {viewMode === "tasks" ? (
                <SimpleGrid
                    items={items}
                    columns={columns}
                    getRowId={(item) => idString(item.id)}
                />
            ) : (
                <SimpleGrid
                    items={sessions}
                    columns={sessionColumns}
                    getRowId={(item) => item.id?.toString() || ""}
                />
            )}
        </div>
    );
};
